import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Shield, Zap, Globe, BarChart3, Cpu, ArrowRight, CheckCircle2, TrendingUp, AlertTriangle, Server } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
export function Home() {
  return <div className="flex flex-col min-h-screen" data-id="element-204">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden" data-id="element-205">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/20 via-slate-950 to-slate-950" data-id="element-206" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-207">
          <div className="text-center max-w-4xl mx-auto" data-id="element-208">
            <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight mb-8 leading-tight" data-id="element-209">
              World's Fastest <br data-id="element-210" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600" data-id="element-211">
                Network Analytics
              </span>
            </h1>
            <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-212">
              Gain unparalleled visibility into your network infrastructure.
              Real-time monitoring, deep packet inspection, and AI-driven threat
              detection at enterprise scale.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-213">
              <Link to="/contact" data-id="element-214">
                <Button size="lg" rightIcon={<ArrowRight className="h-5 w-5" data-id="element-216" />} data-id="element-215">
                  Contact Sales
                </Button>
              </Link>
              <Link to="/features" data-id="element-217">
                <Button variant="secondary" size="lg" data-id="element-218">
                  View Live Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Interactive Dashboard Preview */}
        <div className="mt-20 relative max-w-6xl mx-auto px-4" data-id="element-219">
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl blur opacity-20" data-id="element-220"></div>
          <div className="relative rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden shadow-2xl" data-id="element-221">
            {/* Dashboard Header */}
            <div className="bg-slate-950 border-b border-slate-800 px-6 py-4 flex items-center justify-between" data-id="element-222">
              <div className="flex items-center space-x-4" data-id="element-223">
                <Activity className="h-5 w-5 text-cyan-400" data-id="element-224" />
                <span className="text-white font-semibold" data-id="element-225">
                  Network Overview
                </span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-226">
                <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse" data-id="element-227"></span>
                <span className="text-slate-400 text-sm" data-id="element-228">Live</span>
              </div>
            </div>

            {/* Dashboard Content */}
            <div className="p-6 space-y-6" data-id="element-229">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-230">
                {[{
                label: 'Active Nodes',
                value: '2,847',
                trend: '+12%',
                icon: Server
              }, {
                label: 'Avg Latency',
                value: '23ms',
                trend: '-8%',
                icon: Zap
              }, {
                label: 'Throughput',
                value: '1.2 TB/s',
                trend: '+24%',
                icon: TrendingUp
              }, {
                label: 'Threats Blocked',
                value: '156',
                trend: '+5%',
                icon: Shield
              }].map((stat, idx) => <div key={idx} className="bg-slate-950/50 rounded-lg p-4 border border-slate-800" data-id="element-231">
                    <div className="flex items-center justify-between mb-2" data-id="element-232">
                      <stat.icon className="h-4 w-4 text-slate-500" data-id="element-233" />
                      <span className="text-xs text-green-400" data-id="element-234">
                        {stat.trend}
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-white mb-1" data-id="element-235">
                      {stat.value}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-236">{stat.label}</div>
                  </div>)}
              </div>

              {/* Chart Placeholder */}
              <div className="bg-slate-950/50 rounded-lg p-6 border border-slate-800" data-id="element-237">
                <div className="flex items-center justify-between mb-4" data-id="element-238">
                  <h3 className="text-white font-semibold" data-id="element-239">Network Traffic</h3>
                  <div className="flex space-x-2 text-xs" data-id="element-240">
                    <span className="text-slate-500" data-id="element-241">Last 24h</span>
                  </div>
                </div>
                <div className="h-48 flex items-end justify-between space-x-2" data-id="element-242">
                  {[65, 78, 45, 89, 92, 67, 85, 73, 95, 88, 76, 82, 90, 85, 78, 92, 88, 95, 82, 76].map((height, idx) => <div key={idx} className="flex-1 bg-gradient-to-t from-cyan-500/50 to-cyan-500/10 rounded-t" style={{
                  height: `${height}%`
                }} data-id="element-243"></div>)}
                </div>
              </div>

              {/* Alerts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-244">
                <div className="bg-slate-950/50 rounded-lg p-4 border border-slate-800 flex items-start space-x-3" data-id="element-245">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" data-id="element-246" />
                  <div data-id="element-247">
                    <div className="text-white font-medium text-sm" data-id="element-248">
                      High Latency Detected
                    </div>
                    <div className="text-slate-500 text-xs" data-id="element-249">
                      us-west-2 • 2 minutes ago
                    </div>
                  </div>
                </div>
                <div className="bg-slate-950/50 rounded-lg p-4 border border-slate-800 flex items-start space-x-3" data-id="element-250">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" data-id="element-251" />
                  <div data-id="element-252">
                    <div className="text-white font-medium text-sm" data-id="element-253">
                      DDoS Attack Mitigated
                    </div>
                    <div className="text-slate-500 text-xs" data-id="element-254">
                      eu-central-1 • 15 minutes ago
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 border-y border-slate-800 bg-slate-950/50" data-id="element-255">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-256">
          <p className="text-center text-sm font-semibold text-slate-500 uppercase tracking-wider" data-id="element-257">
            Trusted by forward-thinking engineering teams
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-slate-950" data-id="element-258">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-259">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-260">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-261">
              Everything you need to master your network
            </h2>
            <p className="text-slate-400 text-lg" data-id="element-262">
              Comprehensive tools designed for modern enterprise infrastructure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-263">
            {[{
            icon: <Zap className="h-6 w-6 text-cyan-400" data-id="element-264" />,
            title: 'Real-time Monitoring',
            desc: 'Sub-millisecond latency tracking across your entire global infrastructure.'
          }, {
            icon: <Shield className="h-6 w-6 text-cyan-400" data-id="element-265" />,
            title: 'Threat Detection',
            desc: 'AI-powered anomaly detection identifies security threats before they impact users.'
          }, {
            icon: <BarChart3 className="h-6 w-6 text-cyan-400" data-id="element-266" />,
            title: 'Deep Analytics',
            desc: 'Historical data retention with instant query capabilities for forensic analysis.'
          }, {
            icon: <Globe className="h-6 w-6 text-cyan-400" data-id="element-267" />,
            title: 'Global Coverage',
            desc: 'Monitor points of presence in over 100+ countries with unified reporting.'
          }, {
            icon: <Cpu className="h-6 w-6 text-cyan-400" data-id="element-268" />,
            title: 'Packet Inspection',
            desc: 'Drill down into individual packets to debug complex networking issues.'
          }, {
            icon: <Activity className="h-6 w-6 text-cyan-400" data-id="element-269" />,
            title: 'Custom Dashboards',
            desc: 'Build tailored views for different teams with our drag-and-drop builder.'
          }].map((feature, idx) => <Card key={idx} className="hover:border-cyan-500/50 transition-all duration-300 group hover:shadow-lg hover:shadow-cyan-500/10" data-id="element-270">
                <div className="h-12 w-12 rounded-lg bg-slate-800 flex items-center justify-center mb-6 group-hover:bg-cyan-500/10 transition-colors" data-id="element-271">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3" data-id="element-272">
                  {feature.title}
                </h3>
                <p className="text-slate-400 leading-relaxed" data-id="element-273">{feature.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 border-t border-slate-800 bg-slate-900/30" data-id="element-274">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-275">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-276">
            {[{
            label: 'Networks Monitored',
            value: '10M+'
          }, {
            label: 'Data Processed Daily',
            value: '5PB'
          }, {
            label: 'Uptime Guarantee',
            value: '99.99%'
          }].map((stat, idx) => <div key={idx} className="p-8" data-id="element-277">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2" data-id="element-278">
                  {stat.value}
                </div>
                <div className="text-cyan-500 font-medium" data-id="element-279">{stat.label}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24" data-id="element-280">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-281">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-cyan-900 to-blue-900 p-12 text-center" data-id="element-282">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80')] opacity-10 mix-blend-overlay bg-cover bg-center" data-id="element-283"></div>
            <div className="relative z-10" data-id="element-284">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-285">
                Ready to transform your network visibility?
              </h2>
              <p className="text-cyan-100 text-lg mb-8 max-w-2xl mx-auto" data-id="element-286">
                Join thousands of enterprise teams using njgod to monitor,
                secure, and optimize their infrastructure.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-287">
                <Link to="/contact" data-id="element-288">
                  <Button size="lg" className="bg-white text-blue-900 hover:bg-cyan-50" data-id="element-289">
                    Get Started Now
                  </Button>
                </Link>
                <Link to="/pricing" data-id="element-290">
                  <Button variant="outline" size="lg" className="border-cyan-200 text-cyan-100 hover:bg-white/10 hover:text-white" data-id="element-291">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}